import { useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import useRead from "./useRead";
import usePut from "./usePut";
import Cookies from "universal-cookie";
import { COMPANY_ID } from "../config";

const useTdsDeclaration = () => {
  const { company_employee_id, financial_year_id } = useParams();
  const navigate = useNavigate();
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const cookies = new Cookies();
  const company_id = parseInt(cookies.get(COMPANY_ID));

  // Default form values
  const defaultValues = {
    house_rent_applicable: 0,
    house_loan_applicable: 0,
    rental_income_applicable: 0,
    company_employee_id: parseInt(company_employee_id),
    financial_year_id: parseInt(financial_year_id),
    company_id: company_id,
    house_rent: [
      {
        rent_amount: 0,
        from_date: "",
        to_date: "",
        address: "",
        landlord_name: "",
        landlord_pan: "",
        metro: "metro",
        total_amount: 0,
      },
    ],
    home_loan: {
      principal_amount: 0,
      interest_amount: 0,
      name_of_lender: "",
      lender_pan: "",
    },
    other_source_income: {
      income_from_other_sources: 0,
      interest_from_savings: 0,
      interest_from_fixed_deposits: 0,
      interest_from_national_savings: 0,
    },
    rental_income: [
      {
        annual_rent_received: 0,
        municipal_taxes_paid: 0,
        net_annual_value: 0,
        standard_deduction: 0,
        repaying_home_loan: false,
        repaying_home_loan_data: {
          principal_amount: 0,
          interest_amount: 0,
          name_of_lender: "",
          lender_pan: "",
        },
        net_income_loss: 0,
      },
    ],
    "80C_investments": [],
    exemptions_80d: [],
    other_investments: [],
  };

  const { control, handleSubmit, watch, setValue, reset } = useForm({
    defaultValues,
    mode: "onChange",
  });

  // Fetch existing TDS data
  const { list: existingData } = useRead({
    url: "tds/get-user-response",
    initialData: {
      company_employee_id: parseInt(company_employee_id),
      screen: "DECLARATION",
      financial_year_id: parseInt(financial_year_id),
    },
    method: "GET",
    onSuccess: (data) => {
      if (data?.data) {
        transformAndSetFormData(data.data);
        setIsDataLoaded(true);
      }
    },
    onError: (error) => {
      console.error("Error fetching TDS data:", error);
      toast.error("Error loading declaration data");
      setIsDataLoaded(true);
    },
  });

  // Transform API response to form format
  const transformAndSetFormData = (apiData) => {
    const {
      main_submission_data,
      house_rent = [],
      home_loan,
      other_source_income,
      rental_income = [],
      investments_80C = [],
      exemptions_80D = [],
      other_investments = [],
    } = apiData;

    const financialYearId =
      main_submission_data?.financial_year?.financial_year_id || 1;

    // Helper function to convert API date to Date object for month picker
    const convertApiDateToMonthPicker = (dateString) => {
      if (!dateString) return null;

      // Handle both "2025-01-31 18:30:00" and "2025-01-31" formats
      const dateOnly = dateString.split(" ")[0]; // Get "2025-01-31"
      const [year, month, day] = dateOnly.split("-");

      // Create date object - month is 0-based in JavaScript Date
      return new Date(parseInt(year), parseInt(month) - 1, 1);
    };

    // Helper function to calculate months difference
    const calculateMonthsDifference = (fromDate, toDate) => {
      if (!fromDate || !toDate) return 0;
      const from = new Date(fromDate);
      const to = new Date(toDate);
      const yearDiff = to.getFullYear() - from.getFullYear();
      const monthDiff = to.getMonth() - from.getMonth();
      return yearDiff * 12 + monthDiff + 1;
    };

    // Transform house rent data
    const transformedHouseRent =
      house_rent.length > 0
        ? house_rent.map((item) => {
            const months = calculateMonthsDifference(
              item.from_date,
              item.to_date
            );
            const monthlyAmount = parseFloat(item.rent_amount || 0);

            return {
              house_rent_id: item.house_rent_id,
              rent_amount: monthlyAmount,
              financial_year_id: financialYearId,
              from_date: convertApiDateToMonthPicker(item.from_date),
              to_date: convertApiDateToMonthPicker(item.to_date),
              address: item.address || "",
              landlord_name: item.landlord_name || "",
              landlord_pan: item.landlord_pan || "",
              metro: item.is_metro === "1" ? "metro" : "non-metro",
              total_amount: monthlyAmount * months, // Fixed: Use actual months calculation
            };
          })
        : [
            {
              ...defaultValues.house_rent[0],
              financial_year_id: financialYearId,
            },
          ];

    // Transform rental income data
    const transformedRentalIncome =
      rental_income.length > 0
        ? rental_income.map((item) => ({
            rental_income_id: item.rental_income_id,
            financial_year_id: financialYearId,
            annual_rent_received: parseFloat(item.annual_rent_received || 0),
            municipal_taxes_paid: parseFloat(item.municipal_taxes_paid || 0),
            net_annual_value: parseFloat(item.net_annual_value || 0),
            standard_deduction: parseFloat(item.standard_deduction || 0),
            repaying_home_loan: item.repaying_home_loan === "1",
            repaying_home_loan_data: {
              principal_amount: parseFloat(item.principal_amount || 0),
              interest_amount: parseFloat(item.interest_amount || 0),
              name_of_lender: item.name_of_lender || "",
              lender_pan: item.lender_pan || "",
            },
            net_income_loss: parseFloat(item.net_income_loss || 0),
          }))
        : [
            {
              ...defaultValues.rental_income[0],
              financial_year_id: financialYearId,
            },
          ];

    // Transform 80C investments
    const transformed80C =
      investments_80C.length > 0
        ? investments_80C.map((item) => ({
            emp_80c_id: item.emp_80c_id,
            investments_80c_id: item.investments_80c_id,
            amount: parseFloat(item.amount || 0),
          }))
        : [];

    // Transform 80D exemptions
    const transformed80D =
      exemptions_80D.length > 0
        ? exemptions_80D.map((item) => ({
            emp_80d_id: item.emp_80d_id,
            exemption_80d_id: item.exemption_80d_id,
            amount: parseFloat(item.amount || 0),
          }))
        : [];

    // Transform other investments
    const transformedOtherInvestments =
      other_investments.length > 0
        ? other_investments.map((item) => ({
            emp_other_investment_id: item.emp_other_investment_id,
            other_investment_id: item.other_investment_id,
            amount: parseFloat(item.amount || 0),
          }))
        : [];

    // Check if home loan has meaningful data
    const hasHomeLoanData =
      home_loan &&
      (parseFloat(home_loan.principal_amount || 0) > 0 ||
        parseFloat(home_loan.interest_amount || 0) > 0 ||
        (home_loan.name_of_lender && home_loan.name_of_lender.trim() !== "") ||
        (home_loan.lender_pan && home_loan.lender_pan.trim() !== ""));

    const formData = {
      employee_tds_submission_id:
        main_submission_data?.employee_tds_submission_id || "",
      employee_regime_selection_id:
        main_submission_data?.employee_regime_selection
          ?.employee_regime_selection_id || null,
      house_rent_applicable: house_rent.length > 0 ? 1 : 0,
      house_loan_applicable: hasHomeLoanData ? 1 : 0, // Changed: Only turn ON if there's meaningful data
      rental_income_applicable: rental_income.length > 0 ? 1 : 0,
      company_employee_id: parseInt(company_employee_id),
      financial_year_id: financialYearId,
      company_id: company_id,
      house_rent: transformedHouseRent,
      home_loan: home_loan
        ? {
            home_loan_id: home_loan.home_loan_id,
            financial_year_id: financialYearId,
            principal_amount: parseFloat(home_loan.principal_amount || 0),
            interest_amount: parseFloat(home_loan.interest_amount || 0),
            name_of_lender: home_loan.name_of_lender || "",
            lender_pan: home_loan.lender_pan || "",
          }
        : {
            ...defaultValues.home_loan,
            financial_year_id: financialYearId,
          },
      other_source_income: other_source_income
        ? {
            other_source_income_id:
              other_source_income.other_source_income_id || "",
            income_from_other_sources: parseFloat(
              other_source_income.income_from_other_sources || 0
            ),
            interest_from_savings: parseFloat(
              other_source_income.interest_from_savings || 0
            ),
            interest_from_fixed_deposits: parseFloat(
              other_source_income.interest_from_fixed_deposits || 0
            ),
            interest_from_national_savings: parseFloat(
              other_source_income.interest_from_national_savings || 0
            ),
          }
        : defaultValues.other_source_income,
      rental_income: transformedRentalIncome,
      "80C_investments": transformed80C,
      exemptions_80d: transformed80D,
      other_investments: transformedOtherInvestments,
    };

    reset(formData);
  };

  // Form submission
  const { handleAuth: submitDeclaration, isLoading: isSubmitting } = usePut({
    url: "tds/user-response",
    onSuccess: () => {
      toast.success("Investment declaration saved successfully");
      navigate(
        `/employee-details/${company_employee_id}/investments-and-proofs`
      );
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Error saving declaration");
    },
  });

  const onSubmit = (data) => {
    // Helper function to convert Date object back to API format
    const convertDateToApiFormat = (date) => {
      if (!date || !(date instanceof Date)) return "";

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    };

    // Transform form data to match API payload format
    const transformedData = {
      ...data,
      house_rent: data.house_rent_applicable === 1 
        ? data.house_rent.map((item) => ({
            ...item,
            house_rent_id: item.house_rent_id || "",
            financial_year_id: data.financial_year_id,
            from_date: convertDateToApiFormat(item.from_date),
            to_date: convertDateToApiFormat(item.to_date),
            metro: item.metro === "metro" ? 1 : 0,
          }))
        : [],
      home_loan: data.house_loan_applicable === 1
        ? {
            ...data.home_loan,
            home_loan_id: data.home_loan.home_loan_id || "",
            financial_year_id: data.financial_year_id,
          }
        : {},
      other_source_income: {
        ...data.other_source_income,
        other_source_income_id:
          data.other_source_income.other_source_income_id || "",
      },
      rental_income: data.rental_income_applicable === 1
        ? data.rental_income.map((item) => ({
            ...item,
            rental_income_id: item.rental_income_id || "",
            financial_year_id: data.financial_year_id,
            repaying_home_loan: item.repaying_home_loan ? 1 : 0,
            repaying_home_loan_data: item.repaying_home_loan
              ? {
                  principal_amount:
                    item.repaying_home_loan_data?.principal_amount || 0,
                  interest_amount:
                    item.repaying_home_loan_data?.interest_amount || 0,
                  name_of_lender:
                    item.repaying_home_loan_data?.name_of_lender || "",
                  lender_pan: item.repaying_home_loan_data?.lender_pan || "",
                }
              : {},
          }))
        : [],
      "80C_investments": data["80C_investments"].map((item) => ({
        ...item,
        emp_80c_id: item.emp_80c_id || "",
      })),
      exemptions_80d: data.exemptions_80d.map((item) => ({
        ...item,
        emp_80d_id: item.emp_80d_id || "",
      })),
      other_investments: data.other_investments.map((item) => ({
        ...item,
        emp_other_investment_id: item.emp_other_investment_id || "",
      })),
    };

    // Wrap the entire payload in employee_regime_data key
    const payload = {
      employee_regime_data: transformedData,
    };

    submitDeclaration.mutate(payload);
  };

  return {
    control,
    handleSubmit,
    watch,
    setValue,
    onSubmit,
    isSubmitting,
    isDataLoaded,
    existingData: existingData?.data?.data,
  };
};

export default useTdsDeclaration;
